import { useCallback, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Watches the points ledger and notifies the student the moment they earn
 *  points or level up. Mounted once inside AppLayout.
 *  Any page can force an immediate check with:
 *    window.dispatchEvent(new Event('points-updated'))
 */
export default function GamificationToaster() {
  const { getAuthHeader, user } = useAuth();
  const seen = useRef(null);          // Set of points_log ids already toasted
  const lastLevel = useRef(null);

  const check = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/gamification/stats`, getAuthHeader ? getAuthHeader() : {});
      const stats = r.data || {};
      const history = stats.points_history || [];

      if (seen.current === null) {
        // First read after mount, establish the baseline silently.
        seen.current = new Set(history.map((h) => h.id));
        lastLevel.current = stats.level;
        window.dispatchEvent(new CustomEvent('gamification-updated', { detail: stats }));
        return;
      }

      const fresh = history.filter((h) => h.id && !seen.current.has(h.id));
      fresh.reverse().forEach((h) => {
        seen.current.add(h.id);
        toast.success(`+${h.points} points · ${h.label || h.action.replace(/_/g, ' ')}`, {
          description: `${h.total_after ?? stats.total_points} points total · Level ${h.level_after ?? stats.level}`,
        });
      });

      if (lastLevel.current != null && stats.level > lastLevel.current) {
        const lvl = (stats.levels || []).find((l) => l.level === stats.level);
        toast.success(`Level up! You're now Level ${stats.level} · ${stats.level_title}`, {
          description: lvl?.reward ? `Unlocked: ${lvl.reward}` : undefined,
          duration: 8000,
        });
      }
      lastLevel.current = stats.level;

      if (fresh.length) {
        window.dispatchEvent(new CustomEvent('gamification-updated', { detail: stats }));
      }
    } catch {
      /* silent, never block the UI on gamification */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!user) return undefined;
    check();
    const interval = setInterval(check, 25000);
    const handler = () => check();
    window.addEventListener('points-updated', handler);
    return () => {
      clearInterval(interval);
      window.removeEventListener('points-updated', handler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  return null;
}
